// The two audio sources of Listen mode. Each goes to the Room only through the
// assistant's own capture (useAssistant), which turns it into 16 kHz PCM frames.
// The recording of a session is separate: lib/listenRecorder.ts.

export type OtherSide =
  | { readonly ok: true; readonly track: MediaStreamTrack; readonly label: string; readonly video: MediaStreamTrack | null }
  | { readonly ok: false; readonly reason: "cancelled" | "noAudio" | "unsupported"; readonly message: string };

export const NO_AUDIO_MESSAGE =
  "No audio came with the share. Pick the Teams tab or the whole screen and turn on Share audio, then try again.";

interface DisplayOptions extends DisplayMediaStreamOptions {
  readonly systemAudio?: "include" | "exclude";
  readonly selfBrowserSurface?: "include" | "exclude";
}

/** Asks for the other side of the call: a tab, window, or screen with its
 * audio. Chrome offers "Share system audio" on a whole-screen share and
 * "Share tab audio" on a browser tab. The video comes with the request (the
 * browser insists on it) and is handed back for the session recording. Must
 * run straight from a click. */
export async function shareOtherSide(): Promise<OtherSide> {
  const media = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
  if (!media?.getDisplayMedia) {
    return { ok: false, reason: "unsupported", message: "This browser cannot share audio from another window. Use Chrome on a computer." };
  }
  const base: Omit<DisplayOptions, "video"> = {
    audio: true,
    systemAudio: "include",
    selfBrowserSurface: "exclude",
  };
  let stream: MediaStream;
  try {
    // The smallest video the picker allows; it is stopped below.
    stream = await media.getDisplayMedia({ ...base, video: { frameRate: { ideal: 5, max: 5 }, width: { max: 1280 }, height: { max: 720 } } } as DisplayOptions);
  } catch (err) {
    const name = (err as Error)?.name;
    if (name === "NotAllowedError" || name === "AbortError") {
      return { ok: false, reason: "cancelled", message: "Sharing was cancelled." };
    }
    try {
      stream = await media.getDisplayMedia({ ...base, video: true } as DisplayOptions);
    } catch (second) {
      const again = (second as Error)?.name;
      return again === "NotAllowedError" || again === "AbortError"
        ? { ok: false, reason: "cancelled", message: "Sharing was cancelled." }
        : { ok: false, reason: "unsupported", message: "This browser could not share audio. Use Chrome on a computer." };
    }
  }
  const track = stream.getAudioTracks()[0];
  if (!track) {
    stopStream(stream);
    return { ok: false, reason: "noAudio", message: NO_AUDIO_MESSAGE };
  }
  // The video stays with the share for the recording (lib/listenRecorder.ts).
  // It is local capture: the other app is not told.
  return { ok: true, track, label: track.label, video: stream.getVideoTracks()[0] ?? null };
}

/** The operator's microphone, with echo control on so the other side's voice
 * from the speakers is not picked up as theirs. */
export async function openMicrophone(): Promise<MediaStreamTrack | null> {
  const media = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
  if (!media?.getUserMedia) return null;
  try {
    const stream = await media.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: false,
    });
    return stream.getAudioTracks()[0] ?? null;
  } catch {
    return null;
  }
}

export function stopTrack(track: MediaStreamTrack | null | undefined): void {
  try {
    track?.stop();
  } catch {
    // Already stopped.
  }
}

export function stopStream(stream: MediaStream | null | undefined): void {
  for (const track of stream?.getTracks() ?? []) stopTrack(track);
}
